import type { Patient } from '@medplum/fhirtypes';
import React, { useMemo, useState } from 'react';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  useToast,
} from '@eluve/components';
import {
  FhirEditScope,
  FhirResourceScope,
  type FhirWriteTransport,
  RenderResource,
  buildResolutionMap,
  formatHumanName,
  isPatientEditValid,
  useFhirEditScope,
  useFhirEditState,
} from '@eluve/fhir-renderer';
import { useMedplum } from '@eluve/medplum-client';

/**
 * The profile's "Edit profile" dialog: the renderer's Patient edit variant
 * inside its own edit scope. Save is gated on dirtiness and the required
 * identity fields; any dismissal with unsaved edits asks for confirmation
 * (there is deliberately no X affordance — Cancel is the way out).
 */

function patientDisplayName(patient: Patient): string {
  const official = patient.name?.find((name) => name.use === 'official');
  return formatHumanName(official ?? patient.name?.[0]) || 'this patient';
}

const DialogBody: React.FC<{
  patient: Patient;
  onSaved: () => void;
  onClose: () => void;
}> = ({ patient, onSaved, onClose }) => {
  const { dirtyCount, commit, reset } = useFhirEditScope();
  const { current } = useFhirEditState(patient);
  const [saving, setSaving] = useState(false);
  const [confirmingDiscard, setConfirmingDiscard] = useState(false);
  const toast = useToast();
  const map = useMemo(() => buildResolutionMap([patient]), [patient]);

  const close = () => {
    setConfirmingDiscard(false);
    reset();
    onClose();
  };

  const requestClose = () => {
    if (dirtyCount > 0) setConfirmingDiscard(true);
    else close();
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const result = await commit();
      if (result.ok) {
        toast.success('Profile updated.');
        onSaved();
        onClose();
      } else if (result.failures.some((f) => f.error.kind === 'conflict')) {
        toast.error(
          'This profile changed since you opened it. Close the dialog and reopen it to edit the latest version.',
        );
      } else {
        toast.error(
          `Couldn't save: ${result.failures[0]?.error.message ?? 'unknown error'}`,
        );
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <DialogContent
      size="lg"
      showCloseButton={false}
      onEscapeKeyDown={(event) => {
        event.preventDefault();
        requestClose();
      }}
      onInteractOutside={(event) => {
        event.preventDefault();
        requestClose();
      }}
    >
      <DialogHeader>
        <DialogTitle>Edit profile</DialogTitle>
        <DialogDescription>
          Update {patientDisplayName(patient)}&apos;s identity, demographics,
          and contact details.
        </DialogDescription>
      </DialogHeader>

      <div className="-mx-1 max-h-[min(60vh,40rem)] scroll-fade overflow-y-auto p-1">
        <FhirResourceScope map={map} placement="modal">
          <RenderResource resource={patient} />
        </FhirResourceScope>
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={requestClose}>
          Cancel
        </Button>
        <Button
          disabled={dirtyCount === 0 || !isPatientEditValid(current) || saving}
          onClick={() => void handleSave()}
          data-testid="save-patient-profile"
        >
          {saving ? 'Saving…' : 'Save changes'}
        </Button>
      </DialogFooter>

      <AlertDialog open={confirmingDiscard} onOpenChange={setConfirmingDiscard}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Discard unsaved changes?</AlertDialogTitle>
            <AlertDialogDescription>
              Your edits to this profile haven&apos;t been saved and will be
              lost if you close now.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep editing</AlertDialogCancel>
            <AlertDialogAction variant="destructive" onClick={close}>
              Discard changes
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DialogContent>
  );
};

export const EditPatientDialog: React.FC<{
  patient: Patient;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved: () => void;
  /** Overridable for stories/tests; defaults to the Medplum SDK client. */
  transport?: FhirWriteTransport;
}> = ({ patient, open, onOpenChange, onSaved, transport }) => {
  const { client } = useMedplum();
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        // Closes route through the body's guarded requestClose only.
        if (next) onOpenChange(true);
      }}
    >
      {open && (
        <FhirEditScope active transport={transport ?? client ?? undefined}>
          <DialogBody
            patient={patient}
            onSaved={onSaved}
            onClose={() => onOpenChange(false)}
          />
        </FhirEditScope>
      )}
    </Dialog>
  );
};
